import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Card, CardBody, Col, Container, Row,
  Badge, Button, Input, Label, Spinner, Modal,
  ModalHeader, ModalBody, ModalFooter, Alert, FormGroup,
} from "reactstrap";
import Breadcrumbs from "components/Common/Breadcrumb";
import { issueDriverPurchaseInvoice, issueDriverBulkPurchaseInvoice, issueCustomerSalesInvoice, getParasutWebLink } from "helpers/parasut_helper";

const BASE_URL = "https://hiflowapi.stechomeyazilim.info";

const ACCEPTED_STATUSES = ["Kabul Edildi", "Onaylandı"];
const DELIVERED_STATUSES = ["TESLIM_ETME", "Tamamlandı"];

const fmtTL = (n) =>
  (Number(n) || 0).toLocaleString("tr-TR", {
    style: "currency",
    currency: "TRY",
    minimumFractionDigits: 2,
  });

const fmtDate = (d) => (d ? new Date(d).toLocaleString("tr-TR") : "-");
const fmtDateShort = (d) => (d ? new Date(d).toLocaleDateString("tr-TR") : "-");

const SurucuHakedisDetail = () => {
  const { id } = useParams();
  const driverId = parseInt(id, 10);
  document.title = "Sürücü Hakediş Detayı | LojGo Admin";

  const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
  const isAdmin = authUser?.Users_Type_ID === 3;
  const isSelfView = !isAdmin && authUser?.ID === driverId;
  const canManage = isAdmin; // sadece admin ödeme kaydı yapabilir

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [alert, setAlert] = useState(null);

  const [driver, setDriver] = useState(null);
  const [jobs, setJobs] = useState([]);     // joined: offer + delivery + advert
  const [payments, setPayments] = useState([]);
  const [invoices, setInvoices] = useState([]); // sürücü için kesilen alış faturaları
  const [salesInvoices, setSalesInvoices] = useState([]); // müşteriye kesilen satış faturaları
  const [invoicingJobId, setInvoicingJobId] = useState(null);
  const [salesInvoicingJobId, setSalesInvoicingJobId] = useState(null);
  const [selectedJobIds, setSelectedJobIds] = useState(new Set()); // toplu fatura seçimi
  const [bulkInvoicing, setBulkInvoicing] = useState(false);

  // payment modal
  const [payModal, setPayModal] = useState(false);
  const [payAmount, setPayAmount] = useState("");
  const [payNote, setPayNote] = useState("");
  const [paySaving, setPaySaving] = useState(false);
  const [payForOffer, setPayForOffer] = useState(null); // {offerId, label, balance} | null

  // delete payment modal
  const [delRow, setDelRow] = useState(null);
  const [delSaving, setDelSaving] = useState(false);

  const showAlert = (type, msg) => {
    setAlert({ type, msg });
    setTimeout(() => setAlert(null), 4000);
  };

  const loadData = useCallback(async () => {
    if (!driverId) return;
    setLoading(true);
    setError(null);
    try {
      const [userRes, deliveriesRes, offersRes, paymentsRes, invoiceRes] = await Promise.all([
        fetch(`${BASE_URL}/Users?$filter=ID eq ${driverId}&$expand=Users_BankAccount&$top=1`),
        fetch(
          `${BASE_URL}/Advert_DeliveryStatus?$filter=Users_ID eq ${driverId} and (Advert_DeliveryStatus_Status eq 'TESLIM_ETME' or Advert_DeliveryStatus_Status eq 'Tamamlandı')&$top=5000`
        ),
        fetch(
          `${BASE_URL}/Advert_Offers?$filter=Users_ID eq ${driverId} and (Offer_Status eq 'Kabul Edildi' or Offer_Status eq 'Onaylandı')&$expand=Advert_Expenses,Advert($expand=Advert_Location,Users)&$top=5000`
        ),
        fetch(`${BASE_URL}/Users_Payment?$filter=Users_ID eq ${driverId}&$orderby=Created_DateTime desc&$top=5000`),
        fetch(`${BASE_URL}/Invoince?$filter=Users_ID eq ${driverId} and Direction eq 'PURCHASE'&$orderby=Created_DateTime desc&$top=5000`)
          .catch(() => ({ ok: false })),
      ]);

      if (!userRes.ok) throw new Error("Sürücü bilgisi alınamadı");

      const [userJ, delivJ, offerJ, payJ] = await Promise.all([
        userRes.json(), deliveriesRes.json(), offersRes.json(), paymentsRes.json(),
      ]);
      const invoiceJ = invoiceRes?.ok ? await invoiceRes.json() : { value: [] };
      setInvoices(invoiceJ.value || []);

      const driverData = userJ?.value?.[0];
      if (!driverData) throw new Error(`#${driverId} ID'li sürücü bulunamadı`);
      setDriver(driverData);
      const deliveries = (delivJ.value || []).filter(d => DELIVERED_STATUSES.includes(d.Advert_DeliveryStatus_Status));
      const offers = (offerJ.value || []).filter(o => ACCEPTED_STATUSES.includes(o.Offer_Status));
      const pays = payJ.value || [];

      const delivByAdvert = new Map();
      deliveries.forEach(d => {
        const cur = delivByAdvert.get(d.Advert_ID);
        if (!cur || new Date(d.Updated_DateTime || d.Created_DateTime) > new Date(cur.Updated_DateTime || cur.Created_DateTime)) {
          delivByAdvert.set(d.Advert_ID, d);
        }
      });

      // İş başına ödenmiş tutar (Advert_Offers_ID ile eşleşen Users_Payment kayıtları)
      const paidByOffer = new Map();
      pays.forEach(p => {
        if (p.Advert_Offers_ID) {
          paidByOffer.set(p.Advert_Offers_ID, (paidByOffer.get(p.Advert_Offers_ID) || 0) + (Number(p.Price) || 0));
        }
      });

      // İş başına kesilen alış faturası
      const invoiceByOffer = new Map();
      (invoiceJ.value || []).forEach(inv => {
        if (inv.Advert_Offers_ID) invoiceByOffer.set(inv.Advert_Offers_ID, inv);
      });

      // Müşteri tarafı satış faturalarını teklif ID'lerine göre çek
      const offerIds = offers.map(o => o.ID);
      const salesInvoiceByOffer = new Map();
      let salesInvList = [];
      if (offerIds.length > 0) {
        const filterClauses = offerIds.map(id => `Advert_Offers_ID eq ${id}`).join(" or ");
        try {
          const salesRes = await fetch(
            `${BASE_URL}/Invoince?$filter=Direction eq 'SALES' and (${filterClauses})&$expand=Users&$orderby=Created_DateTime desc&$top=5000`
          );
          if (salesRes.ok) {
            const salesJ = await salesRes.json();
            salesInvList = salesJ.value || [];
            salesInvList.forEach(inv => {
              if (inv.Advert_Offers_ID) salesInvoiceByOffer.set(inv.Advert_Offers_ID, inv);
            });
          }
        } catch (e) { /* sales fetch hatası kritik değil */ }
      }
      setSalesInvoices(salesInvList);

      const joined = offers
        .filter(o => delivByAdvert.has(o.Advert_ID))
        .map(o => {
          const exps = o.Advert_Expenses || [];
          const approved = exps.filter(e => e.Is_Approved).reduce((s, e) => s + (Number(e.Total_Expense) || 0), 0);
          const pending  = exps.filter(e => !e.Is_Approved).reduce((s, e) => s + (Number(e.Total_Expense) || 0), 0);
          const expense = exps.length > 0 ? approved : (Number(o.Travel_Expenses) || 0);
          // Advert_Offers.Price = sürücünün net teklifi (ekstra kesinti yok)
          const price = Number(o.Price) || 0;
          const loc = o.Advert?.Advert_Location?.[0] || {};
          const deliv = delivByAdvert.get(o.Advert_ID);
          const total = price + expense;
          const paidForJob = paidByOffer.get(o.ID) || 0;
          const invForJob = invoiceByOffer.get(o.ID) || null;
          const salesInvForJob = salesInvoiceByOffer.get(o.ID) || null;
          // Advert.Price = müşterinin ödediği tutar
          const customerPrice = Number(o.Advert?.Price) || 0;
          const customer = o.Advert?.Users || null;
          return {
            offerId: o.ID,
            advertId: o.Advert_ID,
            createdAt: o.Created_DateTime,
            deliveredAt: deliv?.Updated_DateTime || deliv?.Created_DateTime,
            startAddr: loc.Advert_Location_StartAddress || "-",
            finishAddr: loc.Advert_Location_FinishAddress || "-",
            price,
            expense,
            pendingExpense: pending,
            total,
            quickly: o.Quickly,
            paid: paidForJob,
            balance: total - paidForJob,
            invoice: invForJob,
            salesInvoice: salesInvForJob,
            customerPrice,
            customer,
          };
        })
        .sort((a, b) => new Date(b.deliveredAt || 0) - new Date(a.deliveredAt || 0));

      setJobs(joined);
      setPayments(pays);
    } catch (e) {
      console.error(e);
      setError(e.message || "Veriler yüklenirken bir hata oluştu.");
    } finally {
      setLoading(false);
    }
  }, [driverId]);

  useEffect(() => { if (isAdmin || isSelfView) loadData(); }, [isAdmin, isSelfView, loadData]);

  const totals = useMemo(() => {
    const earned = jobs.reduce((s, j) => s + j.total, 0);
    const pendingExp = jobs.reduce((s, j) => s + j.pendingExpense, 0);
    const paid   = payments.reduce((s, p) => s + (Number(p.Price) || 0), 0);
    return { earned, paid, pendingExp, balance: earned - paid };
  }, [jobs, payments]);

  const openPayModal = (job) => {
    if (job) {
      setPayForOffer({ offerId: job.offerId, label: `İlan #${job.advertId}`, balance: job.balance });
      setPayAmount(job.balance > 0 ? job.balance.toFixed(2) : "");
    } else {
      setPayForOffer(null);
      setPayAmount(totals.balance > 0 ? totals.balance.toFixed(2) : "");
    }
    setPayNote("");
    setPayModal(true);
  };

  const submitPayment = async () => {
    const amount = parseFloat(payAmount);
    if (!amount || amount <= 0) {
      showAlert("danger", "Geçerli bir tutar girin.");
      return;
    }
    setPaySaving(true);
    try {
      const body = {
        Users_ID: driverId,
        Price: amount,
        Created_DateTime: new Date().toISOString(),
        Advert_Offers_ID: payForOffer?.offerId || null,
        Note: payNote || null,
      };
      const res = await fetch(`${BASE_URL}/Users_Payment`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error("Ödeme kaydedilemedi");
      setPayModal(false);
      const msg = payForOffer ? `${fmtTL(amount)} (${payForOffer.label}) kaydedildi.` : `${fmtTL(amount)} ödeme kaydedildi.`;
      showAlert("success", msg);
      loadData();
    } catch (e) {
      showAlert("danger", e.message || "Bir hata oluştu");
    } finally {
      setPaySaving(false);
    }
  };

  const toggleJobSelect = (offerId) => {
    setSelectedJobIds((prev) => {
      const next = new Set(prev);
      if (next.has(offerId)) next.delete(offerId);
      else next.add(offerId);
      return next;
    });
  };

  const selectAllInvoiceable = () => {
    const ids = jobs.filter(j => !j.invoice).map(j => j.offerId);
    setSelectedJobIds(new Set(ids));
  };

  const clearSelection = () => setSelectedJobIds(new Set());

  const handleBulkInvoice = async () => {
    if (!driver?.ID) {
      showAlert("danger", "Sürücü bilgileri henüz yüklenmedi. Sayfayı yenile.");
      return;
    }
    const selected = jobs.filter(j => selectedJobIds.has(j.offerId) && !j.invoice);
    if (selected.length === 0) {
      showAlert("warning", "Faturalanabilir iş seçilmedi.");
      return;
    }
    const total = selected.reduce((s, j) => s + j.total, 0);
    if (!window.confirm(`${selected.length} iş için toplam ${fmtTL(total)} tutarında tek alış faturası kesilecek. Onaylıyor musun?`)) return;
    setBulkInvoicing(true);
    try {
      await issueDriverBulkPurchaseInvoice({
        driver,
        jobs: selected.map(j => ({
          offerId: j.offerId,
          advertId: j.advertId,
          amount: j.total,
          quickly: j.quickly,
        })),
      });
      showAlert("success", `${selected.length} iş için toplu fatura kesildi.`);
      clearSelection();
      loadData();
    } catch (e) {
      console.error(e);
      showAlert("danger", e.message || "Toplu fatura kesilirken hata oluştu.");
    } finally {
      setBulkInvoicing(false);
    }
  };

  const handleIssueSalesInvoice = async (job) => {
    if (!job.customer?.ID) {
      showAlert("danger", "Müşteri bilgisi yok — ilana ait kullanıcı bilgisi eksik.");
      return;
    }
    if (!job.customerPrice || job.customerPrice <= 0) {
      showAlert("danger", "Müşteri tutarı (Advert.Price) sıfır veya tanımsız.");
      return;
    }
    if (!window.confirm(`Bu iş için müşteriye ${fmtTL(job.customerPrice)} tutarında satış faturası Paraşüt'te kesilecek. Onaylıyor musun?`)) return;
    setSalesInvoicingJobId(job.offerId);
    try {
      await issueCustomerSalesInvoice({
        customer: job.customer,
        amount: job.customerPrice,
        advertOfferId: job.offerId,
        advertId: job.advertId,
        description: `LojGo Lojistik Hizmet Bedeli — İlan #${job.advertId}${job.quickly ? " (Hızlı)" : ""}`,
        vatRate: 20,
      });
      showAlert("success", "Satış faturası kesildi ve Paraşüt'e gönderildi.");
      loadData();
    } catch (e) {
      console.error(e);
      showAlert("danger", e.message || "Satış faturası kesilirken bir hata oluştu.");
    } finally {
      setSalesInvoicingJobId(null);
    }
  };

  const handleIssueInvoice = async (job) => {
    if (!driver?.ID) {
      showAlert("danger", "Sürücü bilgileri henüz yüklenmedi. Sayfayı yenile.");
      return;
    }
    if (!window.confirm(`Bu iş için ${fmtTL(job.total)} tutarında alış faturası Paraşüt'te kesilecek. Onaylıyor musun?`)) return;
    setInvoicingJobId(job.offerId);
    try {
      await issueDriverPurchaseInvoice({
        driver,
        bankAccount: driver?.Users_BankAccount?.[0],
        amount: job.total,
        advertOfferId: job.offerId,
        advertId: job.advertId,
        description: `LojGo — İlan #${job.advertId} / Teklif #${job.offerId}${job.quickly ? " (Hızlı)" : ""}`,
      });
      showAlert("success", "Alış faturası kesildi ve Paraşüt'e gönderildi.");
      loadData();
    } catch (e) {
      console.error(e);
      showAlert("danger", e.message || "Fatura kesilirken bir hata oluştu.");
    } finally {
      setInvoicingJobId(null);
    }
  };

  const confirmDelete = async () => {
    if (!delRow) return;
    setDelSaving(true);
    try {
      const res = await fetch(`${BASE_URL}/Users_Payment(${delRow.ID})`, { method: "DELETE" });
      if (!res.ok) throw new Error("Ödeme silinemedi");
      setDelRow(null);
      showAlert("success", "Ödeme kaydı silindi.");
      loadData();
    } catch (e) {
      showAlert("danger", e.message || "Bir hata oluştu");
    } finally {
      setDelSaving(false);
    }
  };

  if (!isAdmin && !isSelfView) {
    return (
      <div className="page-content">
        <Container fluid>
          <Alert color="danger">Bu sayfayı görüntüleme yetkiniz yok.</Alert>
        </Container>
      </div>
    );
  }

  const bank = driver?.Users_BankAccount?.[0];
  const typeId = driver?.Users_Type_ID;
  const typeLabel = typeId === 2 ? "Sürücü" : typeId === 4 ? "Çekici" : "-";

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Sürücü Hakedişleri" breadcrumbItem={driver?.Users_NameSurname || `#${driverId}`} />

          {alert && <Alert color={alert.type}>{alert.msg}</Alert>}
          {error && <Alert color="danger">{error}</Alert>}

          {loading ? (
            <div className="text-center py-5"><Spinner /></div>
          ) : (
            <>
              {/* Sürücü kartı */}
              <Row className="mb-3">
                <Col md={4}>
                  <Card><CardBody>
                    <div className="d-flex align-items-center mb-3">
                      <div className="avatar-sm me-3">
                        <span className="avatar-title rounded-circle bg-primary bg-soft text-primary">
                          {(driver?.Users_NameSurname || "?").charAt(0)}
                        </span>
                      </div>
                      <div>
                        <h5 className="mb-1">{driver?.Users_NameSurname || "-"}</h5>
                        <Badge color={typeId === 2 ? "info" : "warning"}>{typeLabel}</Badge>
                      </div>
                    </div>
                    <div className="text-muted small">
                      <div><i className="bx bx-phone me-1" />{driver?.Users_PhoneNumber || "-"}</div>
                      <div><i className="bx bx-mail-send me-1" />{driver?.Users_Email || "-"}</div>
                      <div><i className="bx bx-id-card me-1" />ID: {driverId}</div>
                    </div>
                    {bank ? (
                      <div className="mt-3 pt-3 border-top">
                        <div className="fw-semibold mb-1">Banka Bilgileri</div>
                        <div className="small">
                          <div>{bank.Users_BankAccount_BankName || "-"}</div>
                          <div className="text-muted">{bank.Users_BankAccount_HolderName || "-"}</div>
                          <div className="text-monospace">{bank.Users_BankAccount_Iban || "-"}</div>
                        </div>
                      </div>
                    ) : (
                      <div className="mt-3 pt-3 border-top">
                        <small className="text-warning">
                          <i className="bx bx-error-circle me-1" />
                          Banka bilgisi tanımlı değil
                        </small>
                      </div>
                    )}
                  </CardBody></Card>
                </Col>

                <Col md={8}>
                  <Row>
                    <Col md={4}>
                      <Card><CardBody>
                        <p className="text-muted mb-1 fw-medium">Teslim Edilen İş</p>
                        <h3 className="mb-0">{jobs.length}</h3>
                      </CardBody></Card>
                    </Col>
                    <Col md={4}>
                      <Card><CardBody>
                        <p className="text-muted mb-1 fw-medium">Toplam Hakediş</p>
                        <h3 className="mb-0 text-info">{fmtTL(totals.earned)}</h3>
                        {totals.pendingExp > 0 && (
                          <small className="text-warning">+{fmtTL(totals.pendingExp)} onay bekliyor</small>
                        )}
                      </CardBody></Card>
                    </Col>
                    <Col md={4}>
                      <Card><CardBody>
                        <p className="text-muted mb-1 fw-medium">Bakiye</p>
                        <h3 className={`mb-0 ${totals.balance > 0.009 ? "text-danger" : "text-success"}`}>
                          {fmtTL(totals.balance)}
                        </h3>
                        <small className="text-muted">Ödenmiş: {fmtTL(totals.paid)}</small>
                      </CardBody></Card>
                    </Col>
                  </Row>
                  {canManage && (
                    <div className="text-end">
                      <Button color="success" onClick={() => openPayModal(null)} disabled={totals.balance <= 0.009}>
                        <i className="bx bx-money me-1" /> Toplu Ödeme Yap
                      </Button>
                    </div>
                  )}
                </Col>
              </Row>

              {/* İş kırılımı */}
              <Card>
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="mb-0">Teslim Edilen İşler ({jobs.length})</h5>
                    {canManage && selectedJobIds.size > 0 && (
                      <div className="d-flex gap-2 align-items-center">
                        <small className="text-muted">{selectedJobIds.size} iş seçildi</small>
                        <Button color="link" size="sm" onClick={clearSelection} disabled={bulkInvoicing}>
                          Temizle
                        </Button>
                        <Button color="primary" size="sm" onClick={handleBulkInvoice} disabled={bulkInvoicing}>
                          {bulkInvoicing ? <Spinner size="sm" /> : <><i className="bx bx-receipt me-1" />Toplu Fatura Kes</>}
                        </Button>
                      </div>
                    )}
                  </div>
                  {jobs.length === 0 ? (
                    <div className="text-center py-4 text-muted">
                      Teslim edilmiş iş kaydı bulunamadı.
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table align-middle table-nowrap mb-0">
                        <thead className="table-light">
                          <tr>
                            {canManage && (
                              <th style={{ width: 30 }}>
                                <Input
                                  type="checkbox"
                                  checked={selectedJobIds.size > 0 && jobs.filter(j => !j.invoice).every(j => selectedJobIds.has(j.offerId))}
                                  onChange={(e) => e.target.checked ? selectAllInvoiceable() : clearSelection()}
                                  title="Faturalanabilir tüm işleri seç"
                                />
                              </th>
                            )}
                            <th>İlan</th>
                            <th>Güzergah</th>
                            <th>Teslim</th>
                            <th className="text-end">Sürücü</th>
                            <th className="text-end">Masraf</th>
                            <th className="text-end">Sürücü Top.</th>
                            <th className="text-end">Müşteri</th>
                            <th className="text-end">Ödeme</th>
                            <th className="text-end">Faturalar</th>
                            <th />
                          </tr>
                        </thead>
                        <tbody>
                          {jobs.map(j => {
                            const fullyPaid = j.balance <= 0.009;
                            const partial = j.paid > 0.009 && !fullyPaid;
                            const isInvoicing = invoicingJobId === j.offerId;
                            const isSalesInvoicing = salesInvoicingJobId === j.offerId;
                            const profit = (Number(j.customerPrice) || 0) - j.total;
                            return (
                              <tr key={j.offerId}>
                                {canManage && (
                                  <td>
                                    {!j.invoice && (
                                      <Input
                                        type="checkbox"
                                        checked={selectedJobIds.has(j.offerId)}
                                        onChange={() => toggleJobSelect(j.offerId)}
                                      />
                                    )}
                                  </td>
                                )}
                                <td>
                                  <div className="fw-semibold">#{j.advertId}</div>
                                  {j.quickly && (
                                    <Badge color="warning" pill className="me-1" title="Hızlı teklif">
                                      Hızlı
                                    </Badge>
                                  )}
                                  <small className="text-muted d-block">Teklif #{j.offerId}</small>
                                </td>
                                <td>
                                  <div className="text-truncate" style={{ maxWidth: 280 }} title={j.startAddr}>
                                    <i className="bx bx-current-location text-success me-1" />{j.startAddr}
                                  </div>
                                  <div className="text-truncate" style={{ maxWidth: 280 }} title={j.finishAddr}>
                                    <i className="bx bx-map-pin text-danger me-1" />{j.finishAddr}
                                  </div>
                                </td>
                                <td>{fmtDateShort(j.deliveredAt)}</td>
                                <td className="text-end">{fmtTL(j.price)}</td>
                                <td className="text-end">
                                  {fmtTL(j.expense)}
                                  {j.pendingExpense > 0 && (
                                    <div><small className="text-warning">+{fmtTL(j.pendingExpense)} onay bekliyor</small></div>
                                  )}
                                </td>
                                <td className="text-end fw-semibold">{fmtTL(j.total)}</td>
                                <td className="text-end">
                                  <div className="fw-semibold">{fmtTL(j.customerPrice)}</div>
                                  {j.customer?.ID && (
                                    <div>
                                      <Link to={`/kullanici-aktiviteleri/${j.customer.ID}`} className="small text-decoration-none" title="Müşteri detayını aç">
                                        <i className="bx bx-user me-1" />{j.customer.Users_NameSurname || `#${j.customer.ID}`}
                                      </Link>
                                    </div>
                                  )}
                                  {j.customerPrice > 0 && (
                                    <small className={profit >= 0 ? "text-success" : "text-danger"}>
                                      Kâr: {fmtTL(profit)}
                                    </small>
                                  )}
                                </td>
                                <td className="text-end">
                                  {fullyPaid ? (
                                    <Badge color="success"><i className="bx bx-check me-1" />Ödendi</Badge>
                                  ) : partial ? (
                                    <>
                                      <Badge color="info">Kısmi: {fmtTL(j.paid)}</Badge>
                                      <div><small className="text-danger">Kalan: {fmtTL(j.balance)}</small></div>
                                    </>
                                  ) : (
                                    <Badge color="danger">Ödenmedi</Badge>
                                  )}
                                </td>
                                <td className="text-end">
                                  <div className="d-flex flex-column align-items-end gap-1">
                                    <div>
                                      <small className="text-muted me-1">Alış:</small>
                                      {j.invoice ? (
                                        <Badge color="primary" title={j.invoice.Invoince_Number}>
                                          <i className="bx bx-check me-1" />Kesildi
                                        </Badge>
                                      ) : (
                                        <Badge color="light" className="text-muted">Yok</Badge>
                                      )}
                                    </div>
                                    <div>
                                      <small className="text-muted me-1">Satış:</small>
                                      {j.salesInvoice ? (
                                        <Badge color="success" title={j.salesInvoice.Invoince_Number}>
                                          <i className="bx bx-check me-1" />Kesildi
                                        </Badge>
                                      ) : (
                                        <Badge color="light" className="text-muted">Yok</Badge>
                                      )}
                                    </div>
                                  </div>
                                </td>
                                <td className="text-end">
                                  <div className="d-flex gap-1 justify-content-end">
                                    {canManage && !j.invoice && (
                                      <Button color="primary" size="sm" outline onClick={() => handleIssueInvoice(j)} disabled={isInvoicing} title="Sürücüye alış faturası kes (Paraşüt Giderler)">
                                        {isInvoicing ? <Spinner size="sm" /> : <><i className="bx bx-down-arrow-alt" /></>}
                                      </Button>
                                    )}
                                    {canManage && !j.salesInvoice && (
                                      <Button color="success" size="sm" outline onClick={() => handleIssueSalesInvoice(j)} disabled={isSalesInvoicing || !j.customerPrice} title={!j.customerPrice ? "Müşteri tutarı (Advert.Price) yok" : "Müşteriye satış faturası kes (Paraşüt Satış Faturaları)"}>
                                        {isSalesInvoicing ? <Spinner size="sm" /> : <><i className="bx bx-up-arrow-alt" /></>}
                                      </Button>
                                    )}
                                    {canManage && !fullyPaid && (
                                      <Button color="warning" size="sm" outline onClick={() => openPayModal(j)} title="Bu işin ödemesini yap">
                                        <i className="bx bx-money" />
                                      </Button>
                                    )}
                                    <Link to={`/job-details/${j.advertId}`} className="btn btn-sm btn-light" title="İlanı aç">
                                      <i className="bx bx-link-external" />
                                    </Link>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                          <tr className="table-light fw-bold">
                            <td colSpan={canManage ? 4 : 3} className="text-end">Toplam</td>
                            <td className="text-end">{fmtTL(jobs.reduce((s, j) => s + j.price, 0))}</td>
                            <td className="text-end">{fmtTL(jobs.reduce((s, j) => s + j.expense, 0))}</td>
                            <td className="text-end">{fmtTL(totals.earned)}</td>
                            <td className="text-end">{fmtTL(jobs.reduce((s, j) => s + (Number(j.customerPrice) || 0), 0))}</td>
                            <td className="text-end">{fmtTL(jobs.reduce((s, j) => s + j.paid, 0))} ödenmiş</td>
                            <td className="text-end">
                              <small>Alış: {jobs.filter(j => j.invoice).length}/{jobs.length}</small>
                              <div><small>Satış: {jobs.filter(j => j.salesInvoice).length}/{jobs.length}</small></div>
                            </td>
                            <td />
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardBody>
              </Card>

              {/* Faturalar */}
              <Card>
                <CardBody>
                  <h5 className="mb-3">
                    {isSelfView ? "Kesilen Faturalar" : "Alış Faturaları"} ({invoices.length})
                  </h5>
                  {invoices.length === 0 ? (
                    <div className="text-center py-4 text-muted">
                      Henüz fatura yok.
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table align-middle table-nowrap mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>Fatura No</th>
                            <th>Tarih</th>
                            <th>Bağlı İş</th>
                            <th>Durum</th>
                            <th className="text-end">Tutar</th>
                            <th className="text-end">İşlem</th>
                          </tr>
                        </thead>
                        <tbody>
                          {invoices.map(inv => {
                            const linkedJob = inv.Advert_Offers_ID
                              ? jobs.find(j => j.offerId === inv.Advert_Offers_ID)
                              : null;
                            const parasutUrl = getParasutWebLink(inv.Parasut_Invoice_ID, "PURCHASE");
                            return (
                              <tr key={inv.ID}>
                                <td>
                                  <span className="fw-semibold">{inv.Invoince_Number || `#${inv.ID}`}</span>
                                  {inv.Parasut_Invoice_ID && (
                                    <div><small className="text-muted">Paraşüt: {inv.Parasut_Invoice_ID}</small></div>
                                  )}
                                </td>
                                <td>{fmtDate(inv.Created_DateTime)}</td>
                                <td>
                                  {linkedJob ? (
                                    <Link to={`/job-details/${linkedJob.advertId}`}>İlan #{linkedJob.advertId}</Link>
                                  ) : inv.Advert_Offers_ID ? (
                                    <span className="text-muted">Teklif #{inv.Advert_Offers_ID}</span>
                                  ) : (
                                    <span className="text-muted">-</span>
                                  )}
                                </td>
                                <td>
                                  <Badge color={inv.Parasut_Status === "issued" ? "success" : "secondary"}>
                                    {inv.Invoince_Status || inv.Parasut_Status || "-"}
                                  </Badge>
                                </td>
                                <td className="text-end fw-semibold">{fmtTL(inv.TotalAmount)}</td>
                                <td className="text-end">
                                  {parasutUrl ? (
                                    <a href={parasutUrl} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-primary" title="Paraşüt'te aç">
                                      <i className="bx bx-link-external me-1" />Paraşüt
                                    </a>
                                  ) : (
                                    <span className="text-muted small">-</span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                          <tr className="table-light fw-bold">
                            <td colSpan={4} className="text-end">Toplam</td>
                            <td className="text-end">{fmtTL(invoices.reduce((s, i) => s + (Number(i.TotalAmount) || 0), 0))}</td>
                            <td />
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardBody>
              </Card>

              {/* Müşteri Satış Faturaları */}
              {!isSelfView && (
                <Card>
                  <CardBody>
                    <h5 className="mb-3">
                      <i className="bx bx-up-arrow-alt text-success me-1" />
                      Müşteri Satış Faturaları ({salesInvoices.length})
                    </h5>
                    {salesInvoices.length === 0 ? (
                      <div className="text-center py-4 text-muted">
                        Bu sürücünün işlerine ait müşteri satış faturası yok.
                      </div>
                    ) : (
                      <div className="table-responsive">
                        <table className="table align-middle table-nowrap mb-0">
                          <thead className="table-light">
                            <tr>
                              <th>Fatura No</th>
                              <th>Tarih</th>
                              <th>Müşteri</th>
                              <th>Bağlı İş</th>
                              <th>Durum</th>
                              <th className="text-end">Tutar</th>
                              <th className="text-end">İşlem</th>
                            </tr>
                          </thead>
                          <tbody>
                            {salesInvoices.map(inv => {
                              const linkedJob = inv.Advert_Offers_ID
                                ? jobs.find(j => j.offerId === inv.Advert_Offers_ID)
                                : null;
                              const parasutUrl = getParasutWebLink(inv.Parasut_Invoice_ID, "SALES");
                              const customerName = inv.Users?.Users_NameSurname || inv.Users?.NameSurname || `Müşteri #${inv.Users_ID}`;
                              return (
                                <tr key={inv.ID}>
                                  <td>
                                    <span className="fw-semibold">{inv.Invoince_Number || `#${inv.ID}`}</span>
                                    {inv.Parasut_Invoice_ID && (
                                      <div><small className="text-muted">Paraşüt: {inv.Parasut_Invoice_ID}</small></div>
                                    )}
                                  </td>
                                  <td>{fmtDate(inv.Created_DateTime)}</td>
                                  <td>
                                    {inv.Users_ID ? (
                                      <Link to={`/kullanici-aktiviteleri/${inv.Users_ID}`} title="Müşteri detayını aç">
                                        <i className="bx bx-user me-1" />{customerName}
                                      </Link>
                                    ) : (
                                      customerName
                                    )}
                                  </td>
                                  <td>
                                    {linkedJob ? (
                                      <Link to={`/job-details/${linkedJob.advertId}`}>İlan #{linkedJob.advertId}</Link>
                                    ) : inv.Advert_Offers_ID ? (
                                      <span className="text-muted">Teklif #{inv.Advert_Offers_ID}</span>
                                    ) : (
                                      <span className="text-muted">-</span>
                                    )}
                                  </td>
                                  <td>
                                    <Badge color={inv.Parasut_Status === "issued" ? "success" : "secondary"}>
                                      {inv.Invoince_Status || inv.Parasut_Status || "-"}
                                    </Badge>
                                  </td>
                                  <td className="text-end fw-semibold">{fmtTL(inv.TotalAmount)}</td>
                                  <td className="text-end">
                                    {parasutUrl ? (
                                      <a href={parasutUrl} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-success" title="Paraşüt'te aç">
                                        <i className="bx bx-link-external me-1" />Paraşüt
                                      </a>
                                    ) : (
                                      <span className="text-muted small">-</span>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                            <tr className="table-light fw-bold">
                              <td colSpan={5} className="text-end">Toplam</td>
                              <td className="text-end text-success">{fmtTL(salesInvoices.reduce((s, i) => s + (Number(i.TotalAmount) || 0), 0))}</td>
                              <td />
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    )}
                  </CardBody>
                </Card>
              )}

              {/* Ödeme geçmişi */}
              <Card>
                <CardBody>
                  <h5 className="mb-3">Ödeme Geçmişi ({payments.length})</h5>
                  {payments.length === 0 ? (
                    <div className="text-center py-4 text-muted">
                      Henüz ödeme kaydı yok.
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table align-middle table-nowrap mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>Tarih</th>
                            <th>Tür</th>
                            <th>Not</th>
                            <th className="text-end">Tutar</th>
                            <th />
                          </tr>
                        </thead>
                        <tbody>
                          {payments.map(p => {
                            const linkedJob = p.Advert_Offers_ID
                              ? jobs.find(j => j.offerId === p.Advert_Offers_ID)
                              : null;
                            return (
                              <tr key={p.ID}>
                                <td>{fmtDate(p.Created_DateTime)}</td>
                                <td>
                                  {p.Advert_Offers_ID ? (
                                    linkedJob ? (
                                      <Link to={`/job-details/${linkedJob.advertId}`} className="text-decoration-none">
                                        <Badge color="info">İlan #{linkedJob.advertId}</Badge>
                                      </Link>
                                    ) : (
                                      <Badge color="info">Teklif #{p.Advert_Offers_ID}</Badge>
                                    )
                                  ) : (
                                    <Badge color="secondary">Toplu</Badge>
                                  )}
                                </td>
                                <td><small className="text-muted">{p.Note || "-"}</small></td>
                                <td className="text-end text-success fw-semibold">{fmtTL(p.Price)}</td>
                                <td className="text-end">
                                  {canManage && (
                                    <Button color="link" className="text-danger p-0" size="sm" onClick={() => setDelRow(p)}>
                                      <i className="bx bx-trash" />
                                    </Button>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                          <tr className="table-light fw-bold">
                            <td colSpan={3} className="text-end">Toplam</td>
                            <td className="text-end text-success">{fmtTL(totals.paid)}</td>
                            <td />
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardBody>
              </Card>
            </>
          )}
        </Container>
      </div>

      {/* Ödeme modal */}
      <Modal isOpen={payModal} toggle={() => !paySaving && setPayModal(false)}>
        <ModalHeader toggle={() => !paySaving && setPayModal(false)}>
          {payForOffer ? `Ödeme Kaydet — ${payForOffer.label}` : `Toplu Ödeme — ${driver?.Users_NameSurname}`}
        </ModalHeader>
        <ModalBody>
          <div className="mb-3 p-3 bg-light rounded">
            <div className="d-flex justify-content-between">
              <span className="text-muted">{payForOffer ? "İş için kalan:" : "Toplam açık bakiye:"}</span>
              <strong className="text-danger">{fmtTL(payForOffer ? payForOffer.balance : totals.balance)}</strong>
            </div>
            {payForOffer && (
              <small className="text-muted">
                Bu ödeme yalnızca {payForOffer.label} için kaydedilecek (Advert_Offers_ID = {payForOffer.offerId}).
              </small>
            )}
          </div>
          <FormGroup>
            <Label>Tutar (TL)</Label>
            <Input
              type="number"
              step="0.01"
              min="0.01"
              value={payAmount}
              onChange={(e) => setPayAmount(e.target.value)}
              autoFocus
            />
          </FormGroup>
          <FormGroup>
            <Label>Not (opsiyonel)</Label>
            <Input
              type="textarea"
              rows={2}
              value={payNote}
              onChange={(e) => setPayNote(e.target.value)}
              placeholder="Açıklama, banka, referans no…"
            />
          </FormGroup>
          {bank ? (
            <div className="small text-muted">
              <i className="bx bx-credit-card me-1" />
              {bank.Users_BankAccount_BankName} · {bank.Users_BankAccount_Iban}
            </div>
          ) : (
            <Alert color="warning" className="mb-0 small">
              Sürücünün banka bilgisi tanımlı değil.
            </Alert>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setPayModal(false)} disabled={paySaving}>
            Vazgeç
          </Button>
          <Button color="success" onClick={submitPayment} disabled={paySaving}>
            {paySaving ? <Spinner size="sm" /> : "Ödemeyi Kaydet"}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Silme onay */}
      <Modal isOpen={!!delRow} toggle={() => !delSaving && setDelRow(null)} size="sm">
        <ModalHeader toggle={() => !delSaving && setDelRow(null)}>Ödemeyi Sil</ModalHeader>
        <ModalBody>
          <p className="mb-1">Bu ödeme kaydını silmek istediğinize emin misiniz?</p>
          {delRow && (
            <div className="text-muted small">
              {fmtDate(delRow.Created_DateTime)} — <strong>{fmtTL(delRow.Price)}</strong>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setDelRow(null)} disabled={delSaving}>Vazgeç</Button>
          <Button color="danger" onClick={confirmDelete} disabled={delSaving}>
            {delSaving ? <Spinner size="sm" /> : "Sil"}
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default SurucuHakedisDetail;
